/* Tiny API client for the Cloudflare Pages Functions backend. */

/* ── Run scope ("My runs" vs "All runs") ──────────────────────────
 * A single firm-wide preference that governs every run listing: the
 * agents grid tiles' last-run snapshot, each agent's run history, and
 * the recent-runs timeline. Default 'mine' (a user only ever sees their
 * own + shared passes). An admin can flip to 'all' for firm-wide
 * oversight; the backend ignores ?runScope=all from non-admins, so the
 * preference is harmless if it ever leaks to one. Persisted in
 * localStorage and broadcast so all open run views refetch in step. */
const RUN_SCOPE_KEY = 'eve:runScope';
let _runScope = 'mine';
try { if (localStorage.getItem(RUN_SCOPE_KEY) === 'all') _runScope = 'all'; } catch { /* SSR / no storage */ }

export function getRunScope() { return _runScope; }
export function setRunScope(scope) {
  const next = scope === 'all' ? 'all' : 'mine';
  if (next === _runScope) return;
  _runScope = next;
  try { localStorage.setItem(RUN_SCOPE_KEY, next); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent('eve:runscope', { detail: next })); } catch { /* ignore */ }
}
/** Query suffix ('?runScope=all' or '') for the run-listing endpoints. */
function runScopeQuery() { return _runScope === 'all' ? '?runScope=all' : ''; }

/* ── Resilient fetch ──────────────────────────────────────────────
 * A bare fetch() against a Pages Function can hang indefinitely — most
 * visibly during a deploy rollover, when the edge swaps the Worker and
 * an in-flight request stalls with no response and no error. The SPA's
 * initial data load has no timeout, so a single stalled request traps
 * the user on "Loading…" forever with no recovery. fetchJSON bounds
 * every call with an AbortController timeout and retries transient
 * failures (timeout / network drop / 502-503-504 rollover blips) with
 * a short backoff, so a momentary stall self-heals instead of wedging
 * the page. A 4xx (auth, not-found, bad-request) is returned as-is —
 * those are real answers, not blips, and the caller surfaces them. */
async function fetchJSON(url, { timeoutMs = 15000, retries = 2, ...init } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(url, { credentials: 'same-origin', signal: ctrl.signal, ...init });
      clearTimeout(timer);
      // Retry only on transient gateway/rollover statuses; let the
      // caller handle every other status (incl. 4xx) as a real answer.
      if ((res.status === 502 || res.status === 503 || res.status === 504) && attempt < retries) {
        lastErr = new Error(`gateway ${res.status}`);
      } else {
        return res;
      }
    } catch (err) {
      clearTimeout(timer);
      lastErr = err; // AbortError (timeout) or a network failure
      if (attempt >= retries) break;
    }
    // Backoff before the next attempt: 400ms, 800ms.
    await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
  }
  throw lastErr || new Error('request failed');
}

export async function getHealth() {
  const res = await fetch('/api/health');
  return res.json();
}

/** Live presence: { count, windowMinutes, people: [{ id, name, photoUrl, lastSeenAt }] }. */
export async function getPresence() {
  const res = await fetch('/api/presence', { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`presence → ${res.status}`);
  return res.json();
}

export async function getMe() {
  const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
  if (res.status === 401) return null;
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `GET /api/auth/me → ${res.status}`);
  }
  const { user } = await res.json();
  return user;
}

export async function getMyProfile() {
  const res = await fetch('/api/me/profile', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `profile → ${res.status}`);
  return json;
}

export async function login({ email, password }) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `login failed (${res.status})`);
  return json.user;
}

export async function register({ email, password, name, inviteToken }) {
  const res = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ email, password, name, inviteToken }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `register failed (${res.status})`);
  return json;
}

// Self-service "Forgot password?". Always resolves the same way whether or
// not the email has an account; `delivery` only says if email is configured
// at all ('email' | 'none').
export async function forgotPassword(email) {
  const res = await fetch('/api/auth/forgot', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `request failed (${res.status})`);
  return json;  // { ok, delivery }
}

export async function checkPasswordReset(token) {
  const res = await fetch(`/api/auth/reset?token=${encodeURIComponent(token)}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `reset lookup failed (${res.status})`);
  return json;  // { valid, email? , reason? }
}

export async function resetPassword({ token, password }) {
  const res = await fetch('/api/auth/reset', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ token, password }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `reset failed (${res.status})`);
  return json.user;
}

export async function getInvitation(token) {
  const res = await fetch(`/api/invitations/${encodeURIComponent(token)}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `invitation lookup failed (${res.status})`);
  return json.invitation;
}

export async function listAdminUsers() {
  const res = await fetch('/api/admin/users', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `users → ${res.status}`);
  return json.users;
}

export async function uploadAdminUserPhoto(id, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/admin/users/${encodeURIComponent(id)}/photo`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `upload → ${res.status}`);
  return json;  // { photoUrl, key, sizeBytes }
}

export async function deleteAdminUserPhoto(id) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(id)}/photo`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete photo → ${res.status}`);
  return json;
}

export async function patchAdminUser(id, patch) {
  // Forwards every supported field — role + the profile fields. The
  // earlier signature destructured only `role` and silently dropped
  // name/title/company/bio/linkedinUrl/photoUrl/country, which broke
  // admin-side profile editing in the UserProfileModal.
  const res = await fetch(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch user → ${res.status}`);
  return json.user;
}

export async function deleteAdminUser(id) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete user → ${res.status}`);
  return json;
}

export async function getAdminStats() {
  const res = await fetch('/api/admin/stats', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `stats → ${res.status}`);
  return json;
}

export async function getQueueHealth() {
  const res = await fetch('/api/admin/queue-health', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `queue-health → ${res.status}`);
  return json;
}

export async function listAdminInvitations() {
  const res = await fetch('/api/admin/invitations', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `invitations → ${res.status}`);
  return json.invitations;
}

export async function createAdminInvitations({ emails, role, functionId, note, expiresInDays }) {
  const res = await fetch('/api/admin/invitations', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ emails, role, functionId, note, expiresInDays }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `invite failed (${res.status})`);
  return json.issued;
}

export async function listSkills() {
  const res = await fetch('/api/skills', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `skills → ${res.status}`);
  return json.skills;
}

export async function getSkill(id) {
  const res = await fetch(`/api/skills/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `skill → ${res.status}`);
  return json.skill;
}

export async function createSkill(payload) {
  const res = await fetch('/api/skills', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `create skill → ${res.status}`);
  return json.skill;
}

export async function patchSkill(id, patch) {
  const res = await fetch(`/api/skills/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch skill → ${res.status}`);
  return json.skill;
}

export async function deleteSkill(id) {
  const res = await fetch(`/api/skills/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete skill → ${res.status}`);
  return json;
}

export async function listWorkers() {
  const res = await fetch('/api/workers', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `workers → ${res.status}`);
  return json.workers;
}

export async function getWorker(id) {
  const res = await fetch(`/api/workers/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `worker → ${res.status}`);
  return json.worker;
}

export async function spawnWorker({ skillId, title, inputs }) {
  const res = await fetch('/api/workers', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ skillId, title, inputs }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `spawn → ${res.status}`);
  return json.worker;
}

export async function restartWorker(id) {
  const res = await fetch(`/api/workers/${encodeURIComponent(id)}/restart`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `restart → ${res.status}`);
  return json.worker;
}

export async function deleteWorker(id) {
  const res = await fetch(`/api/workers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete worker → ${res.status}`);
  return json;
}

export async function getUserProfile(id) {
  const res = await fetch(`/api/users/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `user → ${res.status}`);
  return json;  // { user, functions, memberships, viewer }
}

// /api/processes endpoints removed — the process layer was retired in
// favour of intents + agents. See README for the current model.

/* ── Chat config catalogs (mirrored from functions/_lib/models.ts) ── */

// Keep this in lockstep with CLAUDE_MODELS on the server. The picker
// can render from here without an API round-trip; the server is still
// the authority — unknown ids get rewritten to DEFAULT_MODEL on POST.
export const CLAUDE_MODELS = [
  { id: 'claude-fable-5',            label: 'Fable 5',    family: 'fable',  tagline: 'Most powerful + most intelligent — a tier above Opus. Firm standard for generative, design and refinement work.' },
  { id: 'claude-opus-4-8',           label: 'Opus 4.8',   family: 'opus',   tagline: 'Highest Opus-tier reasoning — strong on nuanced strategy work, long synthesis.' },
  { id: 'claude-sonnet-4-6',         label: 'Sonnet 4.6', family: 'sonnet', tagline: 'Balanced default — fast and strong on most consulting tasks.' },
  { id: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5',  family: 'haiku',  tagline: 'Fastest + cheapest — good for routing, simple answers, quick drafts.' },
];
export const DEFAULT_MODEL = 'claude-sonnet-4-6';

export const DESIGN_SYSTEMS = [
  { id: 'eraneos', label: 'Eraneos', isDefault: true },
  { id: 'eve',     label: 'Eve by Eraneos' },
];

/* ── Notifications ───────────────────────────────────────────── */

export async function listNotifications(limit = 50) {
  const res = await fetch(`/api/me/notifications?limit=${limit}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `notifications → ${res.status}`);
  return json;  // { notifications, unread }
}

export async function markNotificationRead(id) {
  const res = await fetch('/api/me/notifications/read', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `read → ${res.status}`);
  return json;
}

export async function markAllNotificationsRead() {
  const res = await fetch('/api/me/notifications/read', {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `read-all → ${res.status}`);
  return json;
}

/* ── Learning agents ─────────────────────────────────────────── */

export async function listAgents() {
  const res = await fetchJSON(`/api/agents${runScopeQuery()}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `agents → ${res.status}`);
  return json;  // { agents, recentRuns }
}

export async function getAgent(id) {
  const res = await fetchJSON(`/api/agents/${encodeURIComponent(id)}${runScopeQuery()}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `agent → ${res.status}`);
  return json;  // { agent, runs }
}

export async function patchAgent(id, body) {
  const res = await fetch(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch agent → ${res.status}`);
  return json.agent;
}

/**
 * Stage a Proposal Builder intake file in R2. Returns
 * `{key, name, mime, size}` — pass these through to runAgentNow's
 * `inputs.rfpFiles` array when launching the run.
 */
export async function uploadProposalIntakeFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/agents/intake/upload', {
    method: 'POST',
    credentials: 'same-origin',
    body: fd,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `intake upload → ${res.status}`);
  return json.file;
}

/**
 * Drop a staged intake file from R2 (called when the user removes
 * a pill before launching the run, so the bytes don't linger as
 * orphans). Idempotent — deleting an already-gone key is a no-op
 * server-side.
 */
export async function deleteProposalIntakeFile(key) {
  const res = await fetch(`/api/agents/intake/upload?key=${encodeURIComponent(key)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (res.status === 204) return;
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `intake delete → ${res.status}`);
  }
}

export async function runAgentNow(id, inputs) {
  const hasInputs = inputs && typeof inputs === 'object' && Object.keys(inputs).length > 0;
  const res = await fetch(`/api/agents/${encodeURIComponent(id)}/run-now`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: hasInputs ? { 'Content-Type': 'application/json' } : undefined,
    body: hasInputs ? JSON.stringify({ inputs }) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `run-now → ${res.status}`);
  // reflection master/system → { run: {...} }
  // custom agent (incl. Proposal Builder) → { kind, spawnedRun, routerError, remindersScheduled? }
  return json;
}

export async function createAgent({ name, promptTemplate, scope, kind, scheduleCron }) {
  const res = await fetch('/api/agents', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ name, promptTemplate, scope, kind, scheduleCron }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `create agent → ${res.status}`);
  return json.agent;
}

export async function deleteAgent(id) {
  const res = await fetch(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete agent → ${res.status}`);
  return json;
}

/* ── Agent card image ────────────────────────────────────────── */

/** Regenerate the agent's hero image on the server (gpt-image-1). Pass an
 *  optional `prompt` to drive the scene; omit it to auto-author from the
 *  agent's task. */
export async function regenerateAgentImage(id, prompt) {
  const res = await fetch(`/api/agents/${encodeURIComponent(id)}/image`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(prompt && prompt.trim() ? { prompt: prompt.trim() } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `regenerate image → ${res.status}`);
  return json.agent;
}

/** Upload a custom hero image (PNG / JPEG / WebP, ≤ 6 MB). */
export async function uploadAgentImage(id, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/agents/${encodeURIComponent(id)}/image`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `upload image → ${res.status}`);
  return json.agent;
}

/** Remove the agent's image (it falls back to the flat black card). */
export async function deleteAgentImage(id) {
  const res = await fetch(`/api/agents/${encodeURIComponent(id)}/image`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete image → ${res.status}`);
  return json.agent;
}

export async function applyAgentRun(agentId, runId) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/apply`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `apply → ${res.status}`);
  return json.run;
}

export async function dismissAgentRun(agentId, runId) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/dismiss`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `dismiss → ${res.status}`);
  return json.run;
}

export async function myActivity(limit = 100) {
  const res = await fetch(`/api/me/activity?limit=${limit}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `activity → ${res.status}`);
  return json.entries;
}

/* ── Intents ──────────────────────────────────────────────────── */

export async function putAdminUserTaxonomies(userId, picks) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/taxonomies`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(picks),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `admin taxonomies → ${res.status}`);
  return json;
}

/* ── agent runs (replaces the old intent endpoints) ─────────────── */

export async function getAgentRun(agentId, runId) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `run → ${res.status}`);
  return json; // { run, steps }
}

/**
 * Mark a run "opened" by the current user, so the Agents view stops
 * surfacing it as needing attention until it next updates. Best-effort:
 * a failure here must never block opening the run, so it swallows errors.
 */
export async function markAgentRunSeen(agentId, runId) {
  if (!agentId || !runId) return;
  try {
    await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/seen`, {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch { /* best-effort — never blocks the open */ }
}

export async function answerAgentRunStep(agentId, runId, stepRunId, answer) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/answer`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ stepRunId, answer }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `answer step → ${res.status}`);
  return json;
}

export async function retryAgentRunStep(agentId, runId, stepRunId) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/retry`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ stepRunId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `retry step → ${res.status}`);
  return json;
}

/**
 * Stop a running agent run. scope='run' kills the whole run; scope='step'
 * (with optional stepIndex) only fails one step and lets the runner
 * advance.
 */
export async function stopAgentRun(agentId, runId, { scope = 'run', stepIndex } = {}) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/stop`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ scope, ...(stepIndex !== undefined ? { stepIndex } : {}) }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `stop run → ${res.status}`);
  return json;
}

export async function analyzeStrategyCascade({ path, oldContent, newContent }) {
  const res = await fetch('/api/strategy/cascade', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ path, oldContent, newContent }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `cascade → ${res.status}`);
  return json;  // { analysisId, summary, cascadeItems, proposals }
}

export async function applyChangeProposal(id) {
  const res = await fetch(`/api/change-proposals/${encodeURIComponent(id)}/apply`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `apply → ${res.status}`);
  return json.proposal;
}

export async function dismissChangeProposal(id) {
  const res = await fetch(`/api/change-proposals/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `dismiss → ${res.status}`);
  return json.proposal;
}

// draftProcess removed — the process layer was retired. To draft a
// new agent, use the Agents view (covered by /api/agents).

export async function generateMyIntro({ extra, tone } = {}) {
  const res = await fetch('/api/me/intro/generate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ extra, tone }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `intro generate → ${res.status}`);
  return json;  // { draft, tone }
}

// Admin-on-behalf-of: draft a bio for another user. Returns the draft
// only — the admin saves it via patchAdminUser({ bio }).
export async function generateAdminUserIntro(userId, { extra, tone } = {}) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/intro/generate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ extra, tone }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `admin intro generate → ${res.status}`);
  return json;  // { draft, tone }
}

// Staffing — list a person's entries (any authenticated viewer).
export async function getUserStaffing(userId) {
  const res = await fetch(`/api/users/${encodeURIComponent(userId)}/staffing`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing → ${res.status}`);
  return json;  // { entries, viewer, note? }
}

// Staffing — admin upsert one entry. Server keys on (user, week, type,
// projectId) so the same call updates an existing row.
export async function upsertAdminUserStaffing(userId, entry) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/staffing`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(entry),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing upsert → ${res.status}`);
  return json.entry;
}

// Staffing — admin delete one entry.
export async function deleteAdminUserStaffing(userId, entryId) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/staffing/${encodeURIComponent(entryId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing delete → ${res.status}`);
  return json;  // { ok }
}

// Staffing — self upsert one of my own entries. Keyed server-side on
// (me, week, type, projectId); projectId must reference a real project.
export async function upsertMyStaffing(entry) {
  const res = await fetch('/api/me/staffing', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(entry),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing upsert → ${res.status}`);
  return json.entry;
}

// Staffing — self delete one of my own entries.
export async function deleteMyStaffing(entryId) {
  const res = await fetch(`/api/me/staffing/${encodeURIComponent(entryId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing delete → ${res.status}`);
  return json;  // { ok }
}

export async function uploadChatFile(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/me/files', {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `upload → ${res.status}`);
  return json;  // { id, key, name, mime, size, url, uploadedAt }
}

export async function deleteChatFile({ key, messageId }) {
  const res = await fetch('/api/me/files/delete', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ key, messageId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete → ${res.status}`);
  return json;  // { ok, removedFromMessage }
}

export async function uploadMyPhoto(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/me/photo', {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `upload → ${res.status}`);
  return json;  // { photoUrl, key, sizeBytes }
}

export async function patchMyProfile(patch) {
  const res = await fetch('/api/me/profile', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch profile → ${res.status}`);
  return json.user;
}

// Profile Advisor — the user's own public-agent setting + completeness.
export async function getMyProfileAdvisor() {
  const res = await fetch('/api/me/profile-advisor', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `profile-advisor → ${res.status}`);
  return json;  // { enabled, completeness }
}

export async function setMyProfileAdvisor(enabled) {
  const res = await fetch('/api/me/profile-advisor', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ enabled }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `profile-advisor → ${res.status}`);
  return json;  // { enabled }
}

// Admin variants — read/toggle another user's Profile Advisor setting.
export async function getUserProfileAdvisor(userId) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/profile-advisor`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `profile-advisor → ${res.status}`);
  return json;  // { enabled, completeness }
}

export async function setUserProfileAdvisor(userId, enabled) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/profile-advisor`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ enabled }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `profile-advisor → ${res.status}`);
  return json;  // { enabled }
}

export async function listAdminFunctions() {
  const res = await fetch('/api/admin/functions', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `functions → ${res.status}`);
  return json.functions;
}

export async function getAdminDigitalTwin() {
  const res = await fetch('/api/admin/digital-twin', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `digital-twin → ${res.status}`);
  return json.people || [];
}

export async function getIndustryProfile(slug) {
  const res = await fetch(`/api/industries/${encodeURIComponent(slug)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `industry → ${res.status}`);
  return json;  // { industry, projects, clients, proposals, viewpoints, viewpointsPath }
}

export async function getServiceProfile(slug) {
  const res = await fetch(`/api/services/${encodeURIComponent(slug)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `service → ${res.status}`);
  return json;  // { service, parent, subServices, projects, clients, offerings, offeringsPath }
}

export async function getCompanyProfile(slug) {
  const res = await fetch(`/api/companies/${encodeURIComponent(slug)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `organization → ${res.status}`);
  return json;  // { company, parent, subCompanies, members, offices, officesPath, documents, viewer, news }
}

export async function getOfficeProfile(slug) {
  const res = await fetch(`/api/offices/${encodeURIComponent(slug)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `office → ${res.status}`);
  return json;  // { office, people, viewer }
}

export async function patchOffice(slug, patch) {
  const res = await fetch(`/api/offices/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch office → ${res.status}`);
  return json.office;
}

export async function uploadOfficePhoto(slug, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/offices/${encodeURIComponent(slug)}/photos`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `office photo → ${res.status}`);
  return json.office;
}

export async function removeOfficePhoto(slug, key) {
  const res = await fetch(`/api/offices/${encodeURIComponent(slug)}/photos?key=${encodeURIComponent(key)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `office photo → ${res.status}`);
  return json.office;
}

/* Manual company-news posts (admin). Body is markdown stored as an
 * OrgRepo file; the optional image goes to R2. See
 * functions/api/admin/companies/[slug]/news.ts. */
export async function createCompanyNews(slug, { headline, body, imageFile }) {
  const form = new FormData();
  form.append('headline', headline);
  if (body) form.append('body', body);
  if (imageFile) form.append('image', imageFile);
  const res = await fetch(`/api/admin/companies/${encodeURIComponent(slug)}/news`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `company news → ${res.status}`);
  return json.post;
}

export async function deleteCompanyNews(slug, path) {
  const res = await fetch(`/api/admin/companies/${encodeURIComponent(slug)}/news?path=${encodeURIComponent(path)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `company news → ${res.status}`);
  return json;
}

/* ── Owner-aware inline edits (admin or entity owner; see
 *    functions/_lib/permissions.ts). These hit the public detail
 *    routes, NOT /api/admin/*, so a non-admin owner can save too. ──── */

export async function patchClient(idOrSlug, patch) {
  const res = await fetch(`/api/clients/${encodeURIComponent(idOrSlug)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch client → ${res.status}`);
  return json.client;
}

export async function patchService(slug, patch) {
  const res = await fetch(`/api/services/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch service → ${res.status}`);
  return json.service;
}

export async function patchIndustry(slug, patch) {
  const res = await fetch(`/api/industries/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch industry → ${res.status}`);
  return json.industry;
}

/* ── Creditors: master list (governance/creditors.md) ───────────────
 * The list is read by any authed user (project + org pickers); admins
 * mutate it via the admin endpoints. addCreditor/removeCreditor return
 * the updated list. removeCreditor throws an Error carrying `.projects`
 * (the projects still using it) when the server blocks the delete. */
export async function listCreditors() {
  const res = await fetch('/api/creditors', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `creditors → ${res.status}`);
  return json.creditors || [];
}

export const adminCreditors = {
  list: async () => {
    const res = await fetch('/api/admin/creditors', { credentials: 'same-origin' });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `creditors → ${res.status}`);
    return json.creditors || [];
  },
  add: async name => {
    const res = await fetch('/api/admin/creditors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ name }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `add creditor → ${res.status}`);
    return json.creditors || [];
  },
  remove: async name => {
    const res = await fetch('/api/admin/creditors', {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ name }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.error || `delete creditor → ${res.status}`);
      err.projects = json.projects || [];
      throw err;
    }
    return json.creditors || [];
  },
};

/* Admin CRUD for staffing / booking categories. Every mutation returns the
 * full list (incl. archived) so the admin table re-renders in one shot. */
export const adminStaffingCategories = {
  list: async () => {
    const res = await fetch('/api/admin/staffing-categories', { credentials: 'same-origin' });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `categories → ${res.status}`);
    return json.categories || [];
  },
  create: async body => {
    const res = await fetch('/api/admin/staffing-categories', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `create category → ${res.status}`);
    return json.categories || [];
  },
  patch: async (slug, body) => {
    const res = await fetch(`/api/admin/staffing-categories/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `update category → ${res.status}`);
    return json.categories || [];
  },
  remove: async slug => {
    const res = await fetch(`/api/admin/staffing-categories/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.error || `delete category → ${res.status}`);
      err.usage = json.usage;
      throw err;
    }
    return json.categories || [];
  },
};

export async function patchCompany(slug, patch) {
  const res = await fetch(`/api/companies/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch organization → ${res.status}`);
  return json.company;
}

// Owner-aware project edit (admin or project member/lead partner). The
// existing patchProject() hits the admin route; this one uses the public
// detail route so owners can save inline.
export async function patchProjectInline(idOrSlug, patch) {
  const res = await fetch(`/api/projects/${encodeURIComponent(idOrSlug)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch project → ${res.status}`);
  return json.project;
}

/* ── Rate card + project T&M budget lines ───────────────────────────
 * The rate card (read-only) drives the daily-rate auto-fill; the budget
 * lines are saved replace-all from the project's Time & Material table. */
export async function getRateCard() {
  const res = await fetch('/api/rate-card', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `rate card → ${res.status}`);
  return json;  // { orgs:[{org,currency,rows:[{level,displayName,dailyRate}]}], defaultOrg, levels }
}

export async function putProjectRateLines(idOrSlug, lines) {
  const res = await fetch(`/api/projects/${encodeURIComponent(idOrSlug)}/rate-lines`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ lines }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `save rate lines → ${res.status}`);
  return json.lines || [];
}

/* Per-project monthly commercial table. GET returns the merged view
 * (months with status + revenue + live staffed days); PUT replace-alls
 * the stored rows. */
export async function getProjectMonthly(idOrSlug) {
  const res = await fetch(`/api/projects/${encodeURIComponent(idOrSlug)}/monthly`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `project monthly → ${res.status}`);
  return json;  // { months, currency, volumeLocal, volumeChf, fxToChf }
}

export async function putProjectMonthly(idOrSlug, months) {
  const res = await fetch(`/api/projects/${encodeURIComponent(idOrSlug)}/monthly`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ months }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `save monthly → ${res.status}`);
  return json;
}

/* News ticker — fed by the Client / Service / Industry master agents. */
export async function listEntityNews(entityType, entityId, limit = 12) {
  const qs = new URLSearchParams({ entityType, entityId, limit: String(limit) });
  const res = await fetch(`/api/news?${qs.toString()}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `news → ${res.status}`);
  return json.items || [];
}

export async function getAdminFunction(id) {
  const res = await fetch(`/api/admin/functions/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `function → ${res.status}`);
  return { function: json.function, members: json.members };
}

export async function createAdminFunction({ name, slug, description, ownerUserId }) {
  const res = await fetch('/api/admin/functions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ name, slug, description, ownerUserId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `create function → ${res.status}`);
  return json.function;
}

export async function patchAdminFunction(id, patch) {
  const res = await fetch(`/api/admin/functions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch function → ${res.status}`);
  return json.function;
}

export async function evaluateAdminFunction(id) {
  const res = await fetch(`/api/admin/functions/${encodeURIComponent(id)}/evaluate`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `evaluate → ${res.status}`);
  return json;  // { function, completenessPct, agenticPct, reasoning, coverage }
}

export async function deleteAdminFunction(id) {
  const res = await fetch(`/api/admin/functions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete function → ${res.status}`);
  return json;
}

export async function addFunctionMember(functionId, { userId }) {
  const res = await fetch(`/api/admin/functions/${encodeURIComponent(functionId)}/members`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ userId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `add member → ${res.status}`);
  return json.members;
}

export async function removeFunctionMember(functionId, userId) {
  const res = await fetch(`/api/admin/functions/${encodeURIComponent(functionId)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `remove member → ${res.status}`);
  return json;
}

/* ── Taxonomies (services / industries / clients) ────────────── */

export async function listTaxonomies() {
  const res = await fetch('/api/taxonomies', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `taxonomies → ${res.status}`);
  return json;  // { services, industries, clients }
}

export async function getMyTaxonomies() {
  const res = await fetch('/api/me/taxonomies', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `my taxonomies → ${res.status}`);
  return json;  // { serviceIds, industryIds, clientIds }
}

export async function putMyTaxonomies({ serviceIds, industryIds, clientIds, projectIds, companyIds }) {
  const res = await fetch('/api/me/taxonomies', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ serviceIds, industryIds, clientIds, projectIds, companyIds }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `save taxonomies → ${res.status}`);
  return json;
}

function adminCrud(kind, pluralKey) {
  return {
    list: async () => {
      const res = await fetch(`/api/admin/${kind}`, { credentials: 'same-origin' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `${kind} → ${res.status}`);
      return json[pluralKey];
    },
    create: async body => {
      const res = await fetch(`/api/admin/${kind}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `create ${kind} → ${res.status}`);
      return json[kind.slice(0, -1)];  // singular key: services → service
    },
    patch: async (id, body) => {
      const res = await fetch(`/api/admin/${kind}/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `patch ${kind} → ${res.status}`);
      return json[kind.slice(0, -1)];
    },
    remove: async id => {
      const res = await fetch(`/api/admin/${kind}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `delete ${kind} → ${res.status}`);
      return json;
    },
  };
}

export const adminServices = adminCrud('services', 'services');
export const adminIndustries = adminCrud('industries', 'industries');
export const adminClients = adminCrud('clients', 'clients');
export const adminProjects = adminCrud('projects', 'projects');
export const adminCompanies = adminCrud('companies', 'companies');
export const adminOffices = adminCrud('offices', 'offices');

/* ── Suggested catalog items (admin) ────────────────────────────
 * Names members gave that didn't match the catalog. Admins create the
 * real item (prefilled) and on success link it back to the suggester. */
export async function listSuggestions() {
  const res = await fetch('/api/admin/suggestions', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `suggestions → ${res.status}`);
  return json.suggestions || [];
}
export async function resolveSuggestion(id, itemId) {
  const res = await fetch(`/api/admin/suggestions/${encodeURIComponent(id)}`, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ action: 'resolve', itemId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `resolve suggestion → ${res.status}`);
  return json;
}
export async function dismissSuggestion(id) {
  const res = await fetch(`/api/admin/suggestions/${encodeURIComponent(id)}`, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ action: 'dismiss' }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `dismiss suggestion → ${res.status}`);
  return json;
}

/* ── Cascade-aware entity delete ────────────────────────────────
 * One engine for deleting any catalog entity and resolving everything
 * that points at it. `previewEntityDelete` returns the impact + the
 * 3-way decisions; `executeEntityDelete` applies the chosen resolutions.
 * kind ∈ project | client | company | service | industry. */
export async function previewEntityDelete(kind, id) {
  const res = await fetch(`/api/admin/cascade/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, {
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete preview → ${res.status}`);
  return json.preview;
}

export async function executeEntityDelete(kind, id, resolutions = {}) {
  const res = await fetch(`/api/admin/cascade/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ resolutions }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete → ${res.status}`);
  return json;
}

/* ── News v2: subscriptions, unified feed, morning briefings ────── */

export async function getMyNewsSubscriptions() {
  const res = await fetch('/api/me/news-subscriptions', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `news subscriptions → ${res.status}`);
  return json;  // { people, project, client, industry, service, company, internal, admin }
}

export async function putMyNewsSubscriptions(picks) {
  const res = await fetch('/api/me/news-subscriptions', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(picks),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `save news subscriptions → ${res.status}`);
  return json;
}

export async function getMyNewsFeed({ days = 7, limit = 80 } = {}) {
  const qs = new URLSearchParams({ days: String(days), limit: String(limit) });
  const res = await fetch(`/api/me/news-feed?${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `news feed → ${res.status}`);
  return json;  // { items, days, limit }
}

export async function listMyMorningBriefings(limit = 30) {
  const res = await fetch(`/api/me/morning-briefings?limit=${limit}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `morning briefings → ${res.status}`);
  return json.briefings || [];
}

export async function getMyMorningBriefing(date) {
  const res = await fetch(`/api/me/morning-briefings/${encodeURIComponent(date)}`, { credentials: 'same-origin' });
  if (res.status === 404) return null;
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `morning briefing → ${res.status}`);
  return json.briefing;
}

export async function listAdminFeedback(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`/api/admin/feedback${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `feedback → ${res.status}`);
  return json.feedback || [];
}

/** decision: 'applied' | 'dismissed' | 'reopen' */
export async function decideAdminFeedback(id, decision, decisionNote) {
  const res = await fetch(`/api/admin/feedback/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ decision, decisionNote: decisionNote || null }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `decide feedback → ${res.status}`);
  return json.feedback;
}

export async function saveAdminFeedbackOptimization(id, optimizationMd) {
  const res = await fetch(`/api/admin/feedback/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ optimizationMd }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `save proposal → ${res.status}`);
  return json.feedback;
}

export async function listProposedIntents(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`/api/admin/proposed-intents${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `proposed intents → ${res.status}`);
  return json.observations || [];
}

/**
 * Promote a proposed-intents observation.
 *
 * @param {string} key
 * @param {string|null} slug
 * @param {object} [opts]
 * @param {'intent'|'agent'} [opts.kind='intent']
 * @param {string} [opts.parentIntentSlug]  only when kind='agent'
 */
export async function promoteIntentObservation(key, slug, opts) {
  const kind = opts?.kind === 'agent' ? 'agent' : 'intent';
  const res = await fetch(`/api/admin/proposed-intents/${encodeURIComponent(key)}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      slug: slug || undefined,
      kind,
      parentIntentSlug: opts?.parentIntentSlug || undefined,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `promote → ${res.status}`);
  return json;
}

export async function dismissIntentObservation(key) {
  const res = await fetch(`/api/admin/proposed-intents/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `dismiss → ${res.status}`);
  return json;
}

export async function generateMyMorningBriefing() {
  const res = await fetch('/api/me/morning-briefings', {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `generate briefing → ${res.status}`);
  return json.briefing;
}

export async function deleteAgentRun(agentId, runId) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/delete`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete run → ${res.status}`);
  return json;
}

// Admin-only: clear the agent's entire run history (all users) plus the
// artefacts those runs produced. In-flight runs are left intact.
export async function clearAgentRuns(agentId) {
  const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/runs/clear`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `clear runs → ${res.status}`);
  return json;
}

export async function listAllFunctions() {
  const res = await fetch('/api/functions', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `functions → ${res.status}`);
  return json.functions;
}

export async function getAgenticReadinessTimeseries() {
  const res = await fetch('/api/admin/agentic-readiness', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `agentic-readiness → ${res.status}`);
  return json.series;
}

export async function listPeople() {
  const res = await fetch('/api/people', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `people → ${res.status}`);
  return json.people;
}

/** Admin-only per-user activity stats, loaded lazily so a slow/failed
 *  aggregation never blocks the directory. Returns a { [userId]: {
 *  actionsTotal, tokensTotal, tokens7d, tokens24h } } map —
 *  actionsTotal = the person's audit-log entry count (their "Log"). */
export async function listPeopleActivity() {
  const res = await fetch('/api/admin/people-activity', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `people-activity → ${res.status}`);
  return json.stats || {};
}

export async function fetchUserInsights(userId) {
  const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  const res = await fetch(`/api/user-insights${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `user-insights → ${res.status}`);
  return json.insights;
}

export async function setUserInsightsOptOut(optOut, userId) {
  const res = await fetch('/api/user-insights', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ optOut, ...(userId ? { userId } : {}) }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `user-insights → ${res.status}`);
  return json.insights;
}

export async function listProjects() {
  const res = await fetch('/api/projects', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `projects → ${res.status}`);
  return json.projects;
}

export async function getProjectProfile(id) {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `project profile → ${res.status}`);
  return json;  // { project, client, service, industry, members, files }
}

export async function uploadProjectFile(projectId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}/files`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `upload → ${res.status}`);
  return json.file;
}

export async function deleteProjectFile(projectId, fileId) {
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete file → ${res.status}`);
  return json;
}

export async function addProjectMember(projectId, userId, role) {
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}/members`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ userId, role: role || null }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `add member → ${res.status}`);
  return json;
}

export async function removeProjectMember(projectId, userId) {
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}/members?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `remove member → ${res.status}`);
  return json;
}

export async function addProjectLeadPartner(projectId, userId) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/lead-partners`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ userId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `add lead partner → ${res.status}`);
  return json;
}

export async function removeProjectLeadPartner(projectId, userId) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/lead-partners?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `remove lead partner → ${res.status}`);
  return json;
}

export async function addProjectManager(projectId, userId) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/managers`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ userId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `add project manager → ${res.status}`);
  return json;
}

export async function removeProjectManager(projectId, userId) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/managers?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `remove project manager → ${res.status}`);
  return json;
}

// Admin: re-price every project's volume_chf from its local volume using
// the fixed governance FX rates. Returns { updated, asOf }.
export async function recalcProjectsChf() {
  const res = await fetch('/api/admin/projects/recalc-chf', {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `recalc CHF → ${res.status}`);
  return json;
}

// Per-item leads (client / service / industry / organization). Projects
// use the dedicated *ProjectLeadPartner helpers above.
export async function listItemLeads(itemType, itemId) {
  const res = await fetch(`/api/item-leads?itemType=${encodeURIComponent(itemType)}&itemId=${encodeURIComponent(itemId)}`, {
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `item leads → ${res.status}`);
  return json.leads;
}

export async function addItemLead(itemType, itemId, userId) {
  const res = await fetch('/api/item-leads', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ itemType, itemId, userId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `add lead → ${res.status}`);
  return json.leads;
}

export async function removeItemLead(itemType, itemId, userId) {
  const res = await fetch(`/api/item-leads?itemType=${encodeURIComponent(itemType)}&itemId=${encodeURIComponent(itemId)}&userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `remove lead → ${res.status}`);
  return json.leads;
}

// Project lifecycle (close-out flow driven by the project-lifecycle card).
export async function closeProject(projectId) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/close`, { method: 'POST', credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `close → ${res.status}`);
  return json;
}

export async function extendProject(projectId, weeks) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/extend`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ weeks }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `extend → ${res.status}`);
  return json;
}

export async function draftProjectReference(projectId) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/draft-reference`, { method: 'POST', credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `draft reference → ${res.status}`);
  return json;
}

export async function convertCurrencyForProject(projectId, { amount, currency, date }) {
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}/convert-currency`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ amount, currency, date }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `convert currency → ${res.status}`);
  return json;  // { rate, amount_chf, as_of, source, confidence }
}

export async function suggestProjectDescription(projectId) {
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}/suggest-description`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `suggest description → ${res.status}`);
  return json;  // { description }
}

export async function patchProject(projectId, patch) {
  const res = await fetch(`/api/admin/projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `patch project → ${res.status}`);
  return json.project;
}

export async function getClientProfile(id) {
  const res = await fetch(`/api/clients/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `client profile → ${res.status}`);
  return json;  // { client, industry, lead, members, projects }
}

export async function uploadClientLogo(clientId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/admin/clients/${encodeURIComponent(clientId)}/logo`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `client logo → ${res.status}`);
  return json.client;
}

export async function removeClientLogo(clientId) {
  const res = await fetch(`/api/admin/clients/${encodeURIComponent(clientId)}/logo`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `client logo → ${res.status}`);
  return json.client;
}

/* Client-scoped logo upload/clear, allowed for the engagement lead as
 * well as admins (canEditClient). Used by the client detail page in
 * edit mode; `handle` may be a slug or id. */
export async function putClientLogo(handle, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/clients/${encodeURIComponent(handle)}/logo`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `client logo → ${res.status}`);
  return json.client;
}

export async function clearClientLogo(handle) {
  const res = await fetch(`/api/clients/${encodeURIComponent(handle)}/logo`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `client logo → ${res.status}`);
  return json.client;
}

export async function listMyFunctions() {
  const res = await fetch('/api/me/functions', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `my functions → ${res.status}`);
  return json.functions;
}

export async function listAuditEntries(limit = 100) {
  const res = await fetch(`/api/admin/audit?limit=${limit}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `audit → ${res.status}`);
  return json.entries;
}

/* ── Per-chat read state (cross-browser) ─────────────────────────── */

export async function fetchChatReads() {
  const res = await fetch('/api/me/chat-reads', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `chat-reads → ${res.status}`);
  return json.reads || {};  // { [chatId]: lastOpenedMs }
}

export async function postChatRead(id, at) {
  const res = await fetch('/api/me/chat-reads', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id, at }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `chat-read → ${res.status}`);
  return json;
}

// Batch upsert — { [chatId]: ms }. Used once on load to propagate any
// read marks that only ever lived in this browser's localStorage up to
// the server, so they sync to the user's other browsers.
export async function postChatReads(reads) {
  const res = await fetch('/api/me/chat-reads', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ reads }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `chat-reads → ${res.status}`);
  return json;
}

export async function fetchUnreadCounts(since) {
  const res = await fetch('/api/me/unread', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ since: since || {} }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `unread → ${res.status}`);
  return json.counts || {};
}

export async function getMyCompliance() {
  const res = await fetch('/api/me/compliance', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `compliance → ${res.status}`);
  return json;  // { functions: [...] }
}

// Admin variant — another user's role-compliance series.
export async function getUserCompliance(userId) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/compliance`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `compliance → ${res.status}`);
  return json;  // { functions: [...] }
}

export async function getMyUsage() {
  const res = await fetch('/api/me/usage', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `usage → ${res.status}`);
  return json;  // { last24h, last7d, total, series, bySource }
}

// Admin variant — another user's token usage (same shape as getMyUsage).
export async function getUserUsage(userId) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/usage`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `usage → ${res.status}`);
  return json;  // { last24h, last7d, total, series, bySource }
}

export async function listMyArtifacts() {
  const res = await fetch('/api/me/artifacts', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `artifacts → ${res.status}`);
  return json.artifacts;
}

export async function listMyGeneratedPdfs() {
  const res = await fetch('/api/me/generated-pdfs', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `pdfs → ${res.status}`);
  return json.pdfs;
}

export async function listMyRuns() {
  const res = await fetch('/api/me/runs', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `runs → ${res.status}`);
  return json.runs;
}

export async function deleteMyArtifact(key) {
  // Encode each path segment so slashes survive into the catchall route.
  const encoded = String(key || '').split('/').map(encodeURIComponent).join('/');
  const res = await fetch(`/api/me/artifacts/${encoded}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete artifact → ${res.status}`);
  return json;
}

// Clear ALL of the caller's artifacts — generated binaries + chat uploads.
export async function clearAllMyArtifacts() {
  const res = await fetch('/api/me/clear-artifacts', {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `clear artifacts → ${res.status}`);
  return json;
}

export async function listFirmFeed(limit = 80) {
  const res = await fetch(`/api/feed?limit=${limit}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `feed → ${res.status}`);
  return json.entries;
}

export async function regenerateAdminInvitation(id) {
  const res = await fetch(`/api/admin/invitations/${encodeURIComponent(id)}/regenerate`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `regenerate → ${res.status}`);
  return json.invitation;  // { id, email, token, expiresAt }
}

// Admin-issued password reset link for a locked-out person. The raw link
// comes back exactly once (tokens are stored hashed); when email delivery
// is configured the person also receives it directly (emailSent).
export async function createAdminResetLink(userId) {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/reset-link`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `reset link → ${res.status}`);
  return json;  // { link, expiresAt, emailSent, emailConfigured }
}

export async function revokeAdminInvitation(id) {
  const res = await fetch(`/api/admin/invitations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `revoke failed (${res.status})`);
  return json;
}

export async function logout() {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
}

export async function getOrgRepoTree() {
  const res = await fetch('/api/orgrepo/tree', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `tree → ${res.status}`);
  return json;
}

export async function getOrgRepoMetrics() {
  const res = await fetch('/api/orgrepo/metrics', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `metrics → ${res.status}`);
  return json;
}

export async function getOrgRepoFile(path) {
  const res = await fetch(`/api/orgrepo/file?path=${encodeURIComponent(path)}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `file → ${res.status}`);
  return json.file;
}

export async function putOrgRepoFile({ path, content, sha, message }) {
  const res = await fetch('/api/orgrepo/file', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ path, content, sha, message }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `commit → ${res.status}`);
  return json;
}

export async function deleteOrgRepoFile(path) {
  const res = await fetch(`/api/orgrepo/file?path=${encodeURIComponent(path)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete → ${res.status}`);
  return json;
}

export async function listChats() {
  const res = await fetch('/api/chats');
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `GET /api/chats → ${res.status}`);
  }
  return res.json();
}

export async function getChatMessages(chatId) {
  const res = await fetch(`/api/chats/${encodeURIComponent(chatId)}/messages`);
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `GET /api/chats/${chatId}/messages → ${res.status}`);
  }
  return res.json();
}

/* Direct messages (human-to-human). Open the 1:1 thread with a user and
 * return its chat id, then post messages into it — they show up in the
 * normal chat history for both people. */
export async function openDirectMessage(userId) {
  const res = await fetch(`/api/users/${encodeURIComponent(userId)}/dm`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `open DM → ${res.status}`);
  return json; // { chatId, chat }
}

export async function postChatMessage(chatId, content) {
  const res = await fetch(`/api/chats/${encodeURIComponent(chatId)}/messages`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ content }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `send message → ${res.status}`);
  return json; // { message }
}

/**
 * Stream a chat reply. Calls onDelta for each text fragment, returns
 * { chatId, text } when the stream completes. Pass `signal` from an
 * AbortController to cancel an in-flight stream — the fetch + the
 * reader loop both bail when the signal aborts.
 */
export async function streamChat({ chatId, content, model, designSystem, attachments, skillIds, voiceMode, onDelta, onStep, signal }) {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ chatId, content, model, designSystem, attachments, skillIds, voiceMode }),
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`POST /api/chat → ${res.status}: ${text}`);
  }
  const newChatId = res.headers.get('x-chat-id') || chatId;
  const assistantMessageId = res.headers.get('x-message-id') || null;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  // Cancel the reader on abort so the loop exits promptly instead
  // of waiting for the next chunk. AbortError still bubbles up.
  const onAbort = () => { reader.cancel().catch(() => {}); };
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        // An SSE event-block can carry both `event:` and `data:` lines.
        // We read the event name so we can route `step` events to the
        // live tool-step pills, and `content_block_delta` to the
        // streaming text. Anything else is ignored.
        const lines = block.split('\n');
        const eventLine = lines.find(l => l.startsWith('event: '));
        const dataLine  = lines.find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        const eventName = eventLine ? eventLine.slice(7).trim() : '';
        let payload;
        try { payload = JSON.parse(dataLine.slice(6)); }
        catch { continue; }
        if (eventName === 'step') {
          onStep?.(payload);
          continue;
        }
        if (payload.type === 'content_block_delta' && payload.delta?.type === 'text_delta') {
          const delta = payload.delta.text || '';
          text += delta;
          onDelta?.(delta);
        }
      }
    }
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
  return { chatId: newChatId, text, assistantMessageId };
}

export async function searchChats(q, limit = 50) {
  const res = await fetch(`/api/chats/search?q=${encodeURIComponent(q)}&limit=${limit}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `search → ${res.status}`);
  return json.chats;
}

export async function evaluateChatReply(chatId, payload = {}) {
  // Pass the freshly-streamed assistant + user text in the body so the
  // eval doesn't have to read from D1 — the assistant message is
  // persisted by /api/chat in a non-awaited promise after the stream
  // closes, and querying D1 too quickly used to 400 with "no
  // assistant reply yet to evaluate".
  const res = await fetch(`/api/chats/${encodeURIComponent(chatId)}/eval`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `evaluate → ${res.status}`);
  return json;  // { solved, confidence, reason, gap, suggestion }
}

export async function deleteChatById(id) {
  const res = await fetch(`/api/chats/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete chat → ${res.status}`);
  return json;
}

/* ─── Staffing ──────────────────────────────────────────────────
 * Endpoints land in functions/api/staffing/. The week window is an
 * ISO-week range (e.g. from='2025-W18', to='2025-W26'). */

export async function listStaffingEntries({ from, to, userId } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to)   qs.set('to', to);
  if (userId) qs.set('userId', userId);
  const res = await fetch(`/api/staffing/entries?${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing entries → ${res.status}`);
  return json.entries || [];
}

export async function upsertStaffingEntry(entry) {
  const res = await fetch('/api/staffing/entries', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(entry),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 409) {
    const err = new Error(json.message || 'conflict');
    err.code = 'conflict';
    err.currentUpdatedAt = json.currentUpdatedAt;
    throw err;
  }
  if (!res.ok) throw new Error(json.error || `staffing entry → ${res.status}`);
  return json;
}

export async function deleteStaffingEntry(id) {
  const res = await fetch(`/api/staffing/entries?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete staffing entry → ${res.status}`);
  return json;
}

export async function fetchStaffingCapacity({ from, to, projectId, userIds } = {}) {
  const qs = new URLSearchParams({ from, to });
  if (projectId) qs.set('projectId', projectId);
  if (Array.isArray(userIds) && userIds.length) qs.set('userIds', userIds.join(','));
  const res = await fetch(`/api/staffing/capacity?${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing capacity → ${res.status}`);
  return json;
}

/* ─── Daily bookings (actuals) ──────────────────────────────────
 * Bookings live in functions/api/staffing/bookings*. The booking grid
 * works in calendar days (YYYY-MM-DD); the rollup answers in ISO weeks
 * so the firm-wide grid can overlay actuals on the plan. */

export async function listBookings({ userId, from, to } = {}) {
  const qs = new URLSearchParams();
  if (userId) qs.set('userId', userId);
  if (from) qs.set('from', from);
  if (to)   qs.set('to', to);
  const res = await fetch(`/api/staffing/bookings?${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `bookings → ${res.status}`);
  return json;  // { bookings, weeklyHours, maxHoursPerDay, canEdit }
}

export async function upsertBooking(booking) {
  const res = await fetch('/api/staffing/bookings', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(booking),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `booking → ${res.status}`);
  return json;
}

export async function deleteBooking(id) {
  const res = await fetch(`/api/staffing/bookings?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `delete booking → ${res.status}`);
  return json;
}

export async function fetchBookingRollup({ from, to, userId } = {}) {
  const qs = new URLSearchParams({ from, to });
  if (userId) qs.set('userId', userId);
  const res = await fetch(`/api/staffing/bookings/rollup?${qs}`, { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `booking rollup → ${res.status}`);
  return json.weeks || [];
}

/* Staffing / booking categories — the admin-managed activity types shared
 * by the plan + booking grid. Public read returns only the active ones. */
export async function listStaffingCategories() {
  const res = await fetch('/api/staffing/categories', { credentials: 'same-origin' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `staffing categories → ${res.status}`);
  return json.categories || [];
}
